import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../salon/components/ExpectedTodayBookings.css";

function bookingSourceLabel(source) {
  const s = String(source || "").toLowerCase();
  if (s.includes("walk")) return "Walk-in";
  if (s.includes("online") || s.includes("web") || s.includes("app")) return "Online";
  if (!s) return "Booking";
  return source;
}

function appointmentId(booking) {
  return booking.appointment_id || booking.id;
}

function lineSort(a, b) {
  const ta = String(a.starts_at || "");
  const tb = String(b.starts_at || "");
  if (ta !== tb) return ta < tb ? -1 : 1;
  const la = Number(a.line_no) || 0;
  const lb = Number(b.line_no) || 0;
  if (la !== lb) return la - lb;
  return (Number(appointmentId(a)) || 0) - (Number(appointmentId(b)) || 0);
}

/** One desk row per booking. A line with no booking_id stays its own row (appt-${id}). */
function groupBookings(rows) {
  const groups = [];
  const byKey = new Map();
  for (const booking of rows) {
    const id = appointmentId(booking);
    const key = booking.booking_id != null && booking.booking_id !== ""
      ? `booking-${booking.booking_id}`
      : `appt-${id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, lines: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.lines.push(booking);
  }
  for (const group of groups) {
    group.lines.sort(lineSort);
  }
  return groups;
}

/**
 * Today's Customer Engagement appointments (shared by stylist-checkin + partner live floor).
 * @param {{
 *   fetchBookings: () => Promise<Array>,
 *   onCheckIn: (booking) => void,
 *   onOpenVisit?: (booking, checkinMeta) => void,
 *   compact?: boolean,
 *   className?: string,
 *   pollMs?: number,
 *   allAppointmentsHref?: string,
 * }} props
 */
export default function EngagementExpectedToday({
  fetchBookings,
  onCheckIn,
  onOpenVisit,
  compact = false,
  className = "",
  pollMs = 12000,
  allAppointmentsHref = "",
}) {
  const [bookings, setBookings] = useState([]);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const rows = await fetchBookings();
      setBookings(Array.isArray(rows) ? rows : []);
      setLoadError("");
    } catch (err) {
      setLoadError(err?.message || "Could not load bookings");
    }
  }, [fetchBookings]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, pollMs);
    return () => window.clearInterval(timer);
  }, [load, pollMs]);

  if (!bookings.length && !loadError) {
    return null;
  }

  const rootClass = [
    "salon-expected-bookings",
    compact ? "salon-expected-bookings--compact" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={rootClass} aria-label="Expected today">
      <div className="salon-expected-bookings__head">
        <h2 className="salon-expected-bookings__title">Expected today</h2>
        {!compact ? (
          <p className="salon-expected-bookings__lede">
            Customer Engagement appointments for this outlet. Tap to check in the guest.
          </p>
        ) : null}
        {allAppointmentsHref ? (
          <p className="salon-expected-bookings__lede">
            <Link to={allAppointmentsHref} className="underline font-semibold">
              Open full appointments desk
            </Link>
          </p>
        ) : null}
      </div>
      {loadError ? (
        <p className="salon-expected-bookings__error" role="status">{loadError}</p>
      ) : null}
      {bookings.length ? (
        <ul className="salon-service-lobby__list salon-expected-bookings__list">
          {groupBookings(bookings).map((group) => {
            const earliest = group.lines[0];
            const checkedIn = group.lines.find((line) => line.checkin_id) || null;
            const hasCheckin = Boolean(checkedIn);
            const single = group.lines.length === 1;
            return (
              <li key={group.key}>
                <button
                  type="button"
                  className="salon-expected-bookings__row"
                  onClick={() => {
                    if (hasCheckin && onOpenVisit) {
                      onOpenVisit(checkedIn, {
                        id: checkedIn.checkin_id,
                        customer_name: checkedIn.customer_name,
                        mobile: checkedIn.customer_phone,
                      });
                      return;
                    }
                    onCheckIn(earliest);
                  }}
                >
                  <div className="salon-expected-bookings__meta">
                    <strong>
                      {single && earliest.time_label ? `${earliest.time_label} · ` : ""}
                      {earliest.customer_name || "Guest"}
                    </strong>
                    {single ? (
                      <span>
                        {earliest.service_name || "Service"}
                        {earliest.staff_name ? ` · ${earliest.staff_name}` : ""}
                        {" · "}
                        {bookingSourceLabel(earliest.source)}
                        {hasCheckin ? " · Checked in" : ""}
                      </span>
                    ) : (
                      <>
                        <span className="salon-expected-bookings__lines">
                          {group.lines.map((line) => (
                            <span key={appointmentId(line)} className="salon-expected-bookings__line">
                              {line.time_label ? `${line.time_label} · ` : ""}
                              {line.service_name || "Service"}
                              {line.staff_name ? ` · ${line.staff_name}` : ""}
                            </span>
                          ))}
                        </span>
                        <span>
                          {bookingSourceLabel(earliest.source)}
                          {hasCheckin ? " · Checked in" : ""}
                        </span>
                      </>
                    )}
                  </div>
                  <span className="salon-expected-bookings__cta">
                    {hasCheckin ? "Open" : "Check in"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
